import  { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Button from "../../components/shared/Button";
import { FaStarOfLife, FaWhatsapp, FaCheckCircle } from "react-icons/fa";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000/api";
const WaitForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState("");
  const [error, setError] = useState("");

  // Handle form submission
  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setError("");

    try {
      const response = await axios.post(`${API_URL}/waitlist/join`, values);

      const whatsappGroupLink =
        response.data.whatsappLink ||
        "https://chat.whatsapp.com/CWOlPKeFYxdJpDvd8QZYaq";
      setWhatsappLink(whatsappGroupLink);

      try {
        await axios.post(`${API_URL}/send-email`, {
          email: values.email,
          firstName: values.firstName,
          whatsappLink: whatsappGroupLink,
        });

        toast.info("Welcome email sent successfully!");
      } catch (emailError) {
        console.error("Error sending email:", emailError);
        toast.warning(
          "We couldn't send you a welcome email. Please check your email address."
        );
      }

      try {
        await axios.post(`${API_URL}/add-to-email-list`, {
          email: values.email,
          firstName: values.firstName,
          phoneNo: values.phoneNo,
        });
      } catch (listError) {
        console.error("Error adding to email list:", listError);
      }

      setSubmitSuccess(true);
      toast.success("Successfully joined the waitlist!");

      setTimeout(() => {
        window.open(whatsappGroupLink, "_blank");
      }, 3000);
    } catch (error) {
      console.error("Error submitting form:", error);
      setError(
        error.response?.data?.message ||
          "There was an error processing your request. Please try again."
      );
      toast.error(
        error.response?.data?.message ||
          "There was an error processing your request. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNo: "",
      country: "",
      interest: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().max(40).required("First Name is required"),
      lastName: Yup.string().max(40).required("Last Name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      phoneNo: Yup.string()
        .min(10, "Phone Number must be at least 10 characters")
        .required("Phone Number is required"),
      country: Yup.string().required("Country is required"),
      interest: Yup.string(),
    }),
    onSubmit: (values) => handleSubmit(values),
  });

  useEffect(() => {
    const showValidationErrors = () => {
      const touchedFields = Object.keys(formik.touched);
      for (const field of touchedFields) {
        if (formik.errors[field]) {
          toast.error(`${formik.errors[field]}`, {
            toastId: `error-${field}`,
          });
          return; 
        }
      }
    };

    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      showValidationErrors();
    }
  }, [formik.submitCount, formik.touched, formik.errors]);


  if (submitSuccess) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <ToastContainer position="top-center" />
        <div className="flex flex-col items-center space-y-4">
          <FaCheckCircle className="text-6xl text-green-500" />
          <h3 className="text-2xl font-bold text-slate-800">
            Thank You for Joining Our Waitlist!
          </h3>
          <p className="text-lg text-slate-600 max-w-md">
            We've sent you an email with more information about our program and
            a link to join our WhatsApp community.
          </p>
          <div className="mt-6">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg transition duration-300"
            >
              <FaWhatsapp className="mr-2 text-xl" />
              Join Our WhatsApp Group
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="bg-white rounded-lg shadow-lg p-6 sm:p-8"
    >
      {/* Toast Container */}
      <ToastContainer position="top-center" />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* First Name */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <label
              className="text-lg font-bold text-slate-700"
              htmlFor="firstName"
            >
              First Name
            </label>
            <FaStarOfLife className="text-red-500 text-xs" />
          </div>
          <input
            id="firstName"
            name="firstName"
            value={formik.values.firstName}
            placeholder="E.g. Ahmed"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            type="text"
            className="border outline-none bg-slate-50 rounded-md p-3 focus:ring-2 focus:ring-blue-400"
          />
          {formik.touched.firstName && formik.errors.firstName ? (
            <div className="text-red-600 text-sm bg-red-50 p-1 rounded">
              {formik.errors.firstName}
            </div>
          ) : null}
        </div>

        {/* Last Name */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <label
              className="text-lg font-bold text-slate-700"
              htmlFor="lastName"
            >
              Last Name
            </label>
            <FaStarOfLife className="text-red-500 text-xs" />
          </div>
          <input
            id="lastName"
            name="lastName"
            value={formik.values.lastName}
            placeholder="E.g. Ibrahim"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            type="text"
            className="border outline-none bg-slate-50 rounded-md p-3 focus:ring-2 focus:ring-blue-400"
          />
          {formik.touched.lastName && formik.errors.lastName ? (
            <div className="text-red-600 text-sm bg-red-50 p-1 rounded">
              {formik.errors.lastName}
            </div>
          ) : null}
        </div>

        {/* Email */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <label className="text-lg font-bold text-slate-700" htmlFor="email">
              Email
            </label>
            <FaStarOfLife className="text-red-500 text-xs" />
          </div>
          <input
            id="email"
            name="email"
            value={formik.values.email}
            placeholder="E.g. ahmed.ibrahim@example.com"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            type="email"
            className="border outline-none bg-slate-50 rounded-md p-3 focus:ring-2 focus:ring-blue-400"
          />
          {formik.touched.email && formik.errors.email ? (
            <div className="text-red-600 text-sm bg-red-50 p-1 rounded">
              {formik.errors.email}
            </div>
          ) : null}
          <p className="text-xs text-slate-500">
            We'll send program updates to this email
          </p>
        </div>

        {/* Phone Number */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <label
              className="text-lg font-bold text-slate-700"
              htmlFor="phoneNo"
            >
              WhatsApp Number
            </label>
            <FaStarOfLife className="text-red-500 text-xs" />
          </div>
          <input
            id="phoneNo"
            name="phoneNo"
            value={formik.values.phoneNo}
            placeholder="E.g. +1234567890 (with country code)"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            type="tel"
            className="border outline-none bg-slate-50 rounded-md p-3 focus:ring-2 focus:ring-blue-400"
          />
          {formik.touched.phoneNo && formik.errors.phoneNo ? (
            <div className="text-red-600 text-sm bg-red-50 p-1 rounded">
              {formik.errors.phoneNo}
            </div>
          ) : null}
          <p className="text-xs text-slate-500">
            Your active WhatsApp number with country code
          </p>
        </div>

        {/* Country */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <label
              className="text-lg font-bold text-slate-700"
              htmlFor="country"
            >
              Country
            </label>
            <FaStarOfLife className="text-red-500 text-xs" />
          </div>
          <input
            id="country"
            name="country"
            value={formik.values.country}
            placeholder="E.g. Nigeria"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            type="text"
            className="border outline-none bg-slate-50 rounded-md p-3 focus:ring-2 focus:ring-blue-400"
          />
          {formik.touched.country && formik.errors.country ? (
            <div className="text-red-600 text-sm bg-red-50 p-1 rounded">
              {formik.errors.country}
            </div>
          ) : null}
        </div>

        {/* Interest */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <label
              className="text-lg font-bold text-slate-700"
              htmlFor="interest"
            >
              What interests you most?
            </label>
          </div>
          <select
            id="interest"
            name="interest"
            value={formik.values.interest}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="border outline-none bg-slate-50 rounded-md p-3 focus:ring-2 focus:ring-blue-400"
          >
            <option value="">-- Select an option --</option>
            <option value="Quran Memorization">Quran Memorization</option>
            <option value="Qira'at Training">Qira'at Training</option>
            <option value="Tajweed Rules">Tajweed Rules</option>
            <option value="Arabic Language">Arabic Language</option>
            <option value="All Programs">All Programs</option>
          </select>
        </div>
      </div>

      <div className="mt-8 mb-4">
        <p className="text-sm text-slate-600 mb-6">
          By joining our waitlist, you'll receive priority access to our courses
          when they launch, exclusive content, and updates about our programs.
          You'll also be invited to join our supportive WhatsApp community of
          learners from Maqrahaat Ul Tanzeel.
        </p>
      </div>

      <div className="flex justify-center">
        <Button
          btnBgColor="bg-[#3b76db] hover:bg-[#2a5bb9] transition-colors duration-300"
          btnColor="text-white text-xl"
          btnText={isSubmitting ? "PROCESSING..." : "JOIN WAIT-LIST"}
          padding="py-4 px-8 w-full sm:w-auto"
          disabled={isSubmitting}
        />
      </div>
    </form>
  );
};

export default WaitForm;
